import { ApiLayer } from '@dealstack/frontend/app/components/global/ApiLayer/ApiLayer';
import { useAppSelector } from '@dealstack/frontend/app/hooks/redux';
import { Routing } from '@dealstack/frontend/app/pages/Routing/Routing';
import { store } from '@dealstack/frontend/app/redux/store';
import { getWebEnvironment } from '@dealstack/frontend/shared/environment';
import { DebugValuesProvider } from '@dealstack/frontend/shared/components/DebugMenu/useDebugValues';
import { ModalLayer } from '@dealstack/frontend/shared/components/fundament/Modal/ModalLayer';
import {
	FullLoaderScreenLoaderProvider,
	FullscreenLoader,
} from '@dealstack/frontend/shared/components/loaders/FullLoaderScreenLoader/FullLoaderScreenLoader';
import { AppTypeProvider } from '@dealstack/frontend/shared/mechanism/appTypeContext/appTypeContext';
import { ConfigCatWrapper } from '@dealstack/frontend/shared/mechanism/configCatFrontend/configCatFrontend';
import { CookieScriptComponent } from '@dealstack/frontend/shared/mechanism/cookie-script/cookieScript';
import { setupDebugging } from '@dealstack/frontend/shared/mechanism/debugging/setup';
import { I18NLayer } from '@dealstack/frontend/shared/mechanism/i18n/i18nLayer';
import { logger } from '@dealstack/frontend/shared/mechanism/logger/logger';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { GlobalStyling } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling';
import { AppMantineProvider } from '@dealstack/frontend/shared/mechanism/styleguide/AppMantineProvider';
import { InitializeToastSystem } from '@dealstack/frontend/shared/mechanism/toast/InitializeToastSystem';
import { InitializeTooltips } from '@dealstack/frontend/shared/mechanism/tooltip/InitializeTooltips';
import { PROJECT } from '@dealstack/shared/lib/common/environment';
import { ToastV2Host } from '@dealstack/frontend/shared/mechanism/toast/ToastV2Host';
import * as Sentry from '@sentry/react';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import React, { Suspense } from 'react';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { getGlobalConfig, useGlobalConfig } from '@dealstack/frontend/shared/globalConfig';
import { BroadcastLayer } from '@dealstack/frontend/shared/mechanism/broadcast/broadcast';
import { usePeriodicRefresh } from '@dealstack/frontend/shared/hooks/usePeriodicRefresh/usePeriodicRefresh.hook';
import { SessionTimeoutGuard } from '@dealstack/frontend/shared/components/SessionTimeout/SessionTimeoutGuard';
import { GenericError as GenericErrorPage } from '@dealstack/frontend/shared/pages/GenericError/GenericError';
import { initializeSentry } from '@dealstack/frontend/shared/sentry';
import { HelpBlogPreview } from '@dealstack/frontend/app/pages/HelpBlogPreview/HelpBlogPreview';
import { HELP_BLOG_ROUTES } from '@dealstack/frontend/shared/shared-routes.ts';

// @d.slaby 06.09.2023
// This is set of debugging tools, should not be enabled on production
setupDebugging();

const environment = getWebEnvironment();
const [group] = environment;

initializeSentry({
	dsn: getGlobalConfig().sentryApp,
	apiUrl: getGlobalConfig().apiUrl,
	environment,
	release: import.meta.env.VITE_SENTRY_RELEASE,
});

logger.setConfig({
	enviroment: group,
});

export const InitializeApplication = () => {
	return (
		<React.StrictMode>
			<GlobalStyling />
			<Sentry.ErrorBoundary fallback={<GenericErrorPage />}>
				<FullLoaderScreenLoaderProvider>
					<ThemeProvider theme={defaultTheme}>
						<AppMantineProvider>
							<ModalLayer>
								<Suspense fallback={<FullscreenLoader name="suspense" />}>
									<DebugValuesProvider>
										<Provider store={store}>
											<ToastV2Host />
											<RunInitialConfigurations />
										</Provider>
									</DebugValuesProvider>
								</Suspense>
							</ModalLayer>
						</AppMantineProvider>
					</ThemeProvider>
				</FullLoaderScreenLoaderProvider>
			</Sentry.ErrorBoundary>
		</React.StrictMode>
	);
};

const RunInitialConfigurations = () => {
	const globalState = useAppSelector((state) => state.global);
	const environment = getWebEnvironment();

	const globalConfig = useGlobalConfig();
	const userState = useAppSelector((state) => state.user);

	usePeriodicRefresh(userState);

	const isInsideSanityPreview = window.location.pathname.includes(HELP_BLOG_ROUTES.preview);

	if (!globalConfig) {
		logger.info('[InitializeApplication] Missing GlobalConfig');

		return <GenericErrorPage />;
	}

	if (!globalConfig.configCatSdkKey) {
		throw new Error('ConfigCat SDK missing in GlobalConfig');
	}

	// Render the blog preview with a different route configuration, as Sanity does not support what we currently have
	// Done to avoid changing our current BrowserRouter setup
	if (isInsideSanityPreview) {
		return (
			<ConfigCatWrapper sdkKey={globalConfig.configCatSdkKey}>
				<AppTypeProvider appType={PROJECT.ADMIN}>
					<I18NLayer>
						{globalState?.isLoadingTranslations ? (
							<FullscreenLoader name="translations" />
						) : (
							<ApiLayer>
								<ReactQueryDevtools initialIsOpen={false} />
								<HelpBlogPreview />
							</ApiLayer>
						)}
					</I18NLayer>
				</AppTypeProvider>
			</ConfigCatWrapper>
		);
	}

	return (
		<ConfigCatWrapper sdkKey={globalConfig.configCatSdkKey}>
			<CookieScriptComponent project={PROJECT.ADMIN} environment={environment} />
			<AppTypeProvider appType={PROJECT.ADMIN}>
				<I18NLayer>
					{globalState?.isLoadingTranslations ? (
						<FullscreenLoader name="translations" />
					) : (
						<ApiLayer>
							<ReactQueryDevtools initialIsOpen={false} /> <InitializeTooltips />
							{/* v6 navigated synchronously; v7 defaults to startTransition, which keeps the old route mounted while the next one renders. */}
							<BrowserRouter useTransitions={false}>
								{userState.isLoading ? <FullscreenLoader name="userState" /> : null}
								<Routing />
								<InitializeToastSystem />
								<SessionTimeoutGuard />
								<BroadcastLayer />
							</BrowserRouter>
						</ApiLayer>
					)}
				</I18NLayer>
			</AppTypeProvider>
		</ConfigCatWrapper>
	);
};
